import { PropertiesPanel } from '../../../../../../widgets/propertiesPanel';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { LightConfigHelpers, LightNode, useLightPropertiesCreator } from '../lightsEditorConfig';

// Global styles for light overlay properties panel inputs
const OverlayInputStyles = createGlobalStyle`
	.light-overlay-properties input[type="number"] {
		width: 80px !important;
	}
	
	.light-overlay-properties input[type="text"] {
		width: 80px !important;
	}
	
	/* Escludiamo esplicitamente le checkbox e altri tipi di input */
	.light-overlay-properties input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]) {
		width: 80px !important;
	}
`;

const PanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background: #fff;
`;

const PropertiesPanelContainer = styled.div`
    width: 100%;
`;

// Utility per normalizzare colori hex
const normalizeHexColor = (color: string): string => {
    if (!color) return '#FFFFFF';

    // Se non inizia con #, aggiungilo
    if (!color.startsWith('#')) {
        color = '#' + color;
    }

    // Se non è valido, ritorna bianco
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn('Invalid hex color:', color, 'using white as fallback');
        return '#FFFFFF';
    }

    // Assicurati che sia maiuscolo
    return color.toUpperCase();
};

// Interfaccia semplificata - riceve solo l'oggetto light completo
export interface LightOverlayProps {
    light: {
        id: string;
        name: string;
        lightType: string;
        intensity: number;
        diffuseColor: string;
        specularColor: string;
        groundColor?: string;
        position?: [number, number, number];
        direction?: [number, number, number];
        angle?: number;
        exponent?: number;
    };
    onUpdate: (updates: Partial<LightOverlayProps['light']>) => void;
}

const LightsOverlayComponent: FC<LightOverlayProps> = ({ light, onUpdate }) => {
    console.log('💡 Overlay: Rendering for light:', light.id, light.name);

    // Hook per creare le proprietà delle luci
    const createLightProperties = useLightPropertiesCreator();

    // Stato interno per le proprietà della luce - pattern MaterialOverlayComponent/ObjectOverlayComponent
    const [localLight, setLocalLight] = useState<LightNode>(light as LightNode);

    // Ref per tracciare la luce corrente
    const currentLightIdRef = useRef(light.id);

    // Sincronizza lo stato interno SOLO quando cambia la luce selezionata
    useEffect(() => {
        if (currentLightIdRef.current !== light.id) {
            console.log('🔄 Light changed, syncing local state from:', currentLightIdRef.current, 'to:', light.id);
            currentLightIdRef.current = light.id;
            setLocalLight(light as LightNode);
        }
    }, [light.id]); // Dipende SOLO dall'ID della luce

    const currentValues = useMemo(
        () => ({
            ...localLight,
            name: localLight.name || 'Unnamed Light',
            intensity: typeof localLight.intensity === 'number' ? localLight.intensity : 1,
            diffuseColor: normalizeHexColor(localLight.diffuseColor),
            specularColor: normalizeHexColor(localLight.specularColor),
            groundColor: normalizeHexColor(localLight.groundColor || '#000000'),
            position: localLight.position || ([0, 0, 0] as [number, number, number]),
            direction: localLight.direction || ([0, -1, 0] as [number, number, number]),
            angle: typeof localLight.angle === 'number' ? localLight.angle : 30,
            exponent: typeof localLight.exponent === 'number' ? localLight.exponent : 1
        }),
        [localLight]
    );

    // Helper per aggiornare sia lo stato locale che quello esterno - pattern ObjectOverlayComponent
    const updateProperty = useCallback(
        <K extends keyof LightNode>(key: K, value: LightNode[K]) => {
            console.log('💡 Overlay: Updating', key, 'from', currentValues[key], 'to', value);

            // Aggiorna lo stato locale immediatamente per UI responsiva
            setLocalLight((prev) => ({
                ...prev,
                [key]: value
            }));

            // Valida i colori prima di inviarli
            if (key === 'diffuseColor' || key === 'specularColor' || key === 'groundColor') {
                const normalizedColor = normalizeHexColor(value as string);
                onUpdate({ [key]: normalizedColor } as any);
            } else {
                onUpdate({ [key]: value } as any);
            }
        },
        [onUpdate]
    );

    // Funzione per aggiornare vettori (posizione/direzione) - pattern ObjectOverlayComponent
    const updateVector = useCallback(
        (vectorType: 'position' | 'direction', axis: 'x' | 'y' | 'z', value: number) => {
            const currentVector = currentValues[vectorType];
            const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
            const newVector = [...currentVector] as [number, number, number];
            newVector[axisIndex] = value;

            console.log('💡 Overlay: Updating', vectorType, axis, 'from', currentVector[axisIndex], 'to', value);
            updateProperty(vectorType, newVector);
        },
        [currentValues, updateProperty]
    );

    // Handler per il cambio di colore - gestisce l'interfaccia ColorSelection
    const handleColorChange = useCallback(
        (key: 'diffuseColor' | 'specularColor' | 'groundColor') => {
            return (_: any, color: string) => {
                console.log('💡 Color picker changed:', key, color);
                updateProperty(key, color);
            };
        },
        [updateProperty]
    );

    // Crea gli helpers per la configurazione delle proprietà
    const helpers: LightConfigHelpers = useMemo(
        () => ({
            updateProperty,
            updateVector,
            handleColorChange
        }),
        [updateProperty, updateVector, handleColorChange]
    );

    // Genera le proprietà usando il factory pattern
    const properties = useMemo(() => {
        return createLightProperties(currentValues, helpers);
    }, [createLightProperties, currentValues, helpers]);

    return (
        <>
            <OverlayInputStyles />
            <PanelContainer>
                <PropertiesPanelContainer className='light-overlay-properties'>
                    <PropertiesPanel properties={properties} />
                </PropertiesPanelContainer>
            </PanelContainer>
        </>
    );
};

export default LightsOverlayComponent;
